import { useEffect } from 'react';
import { useSession } from 'next-auth/react';
import useSWR from 'swr';
import { RoleUserOrgAssociationDto } from '@/apiClient';
import { type UserProfileDto } from '@/authApiClient';
import { usePrivate } from '@/components/private/PrivateContext';
import { useGetOrganisationsByUserId } from '@/hooks/useGetOrganisationsByUserId';
import { SESSION_ERROR } from '@/constants/auth';
import { getSessionStorageItem } from '@/lib/browserStorage';
import { logError } from '@/lib/logger';

type UserProfileLoadedCallback = (userProfile: UserProfileDto) => void;
type UserProfileErrorCallback = (hasError: boolean) => void;
type UserOrgAssociationsRetrievedCallback = (
  organisations: RoleUserOrgAssociationDto[],
) => void;

export interface PostLoginFlowProps {
  onUserProfileLoaded: UserProfileLoadedCallback;
  onUserProfileError: UserProfileErrorCallback;
  onUserOrgAssociationsRetrieved: UserOrgAssociationsRetrievedCallback;
}

export function PostLoginFlow({
  onUserProfileLoaded,
  onUserProfileError,
  onUserOrgAssociationsRetrieved,
}: PostLoginFlowProps) {
  const {
    loginFlowComplete,
    userId,
    userProfile,
    userProfileApi,
    setUserOrganisations,
  } = usePrivate();

  const { data: session } = useSession();

  // A session-level failure blocks the profile fetch entirely: bootstrap may
  // have produced no internal user id (`session.error === BOOTSTRAP_ERROR`, so
  // `userId` is undefined and the SWR key stays null), or the token can no
  // longer be refreshed. In these cases the profile request can never run or
  // succeed, so treat it as an error and let the header degrade instead of
  // spinning forever. PrivateGate handles the actual re-auth for token errors.
  const sessionBlocksProfile =
    Boolean(session?.error) || session?.user?.internalUserId == null;

  // `PrivateGate` actively re-authenticates these errors (silent token refresh
  // or redirect to sign-in). While that's in flight the membership list must
  // stay in its "loading" (`undefined`) state — resolving it to `[]` would let
  // `useCurrentOrg`/the registry layout treat the user as having no memberships
  // and redirect to /restricted before recovery finishes.
  const sessionRecovering =
    session?.error === SESSION_ERROR.REFRESH_TOKEN_ERROR ||
    session?.error === SESSION_ERROR.NO_REFRESH_TOKEN;

  // Don't load organisations while the session is blocked. On a bootstrap
  // failure after a token refresh the JWT keeps the prior `internalUserId`, so
  // `userId` is still truthy here — without this guard we'd fetch memberships
  // for that stale id even though the profile is treated as unavailable.
  const { data, error: orgFetchError } = useGetOrganisationsByUserId(userId, {
    shouldFetch: !sessionBlocksProfile,
  });

  const hasSubmittedSurvey =
    userProfile?.surveyResponse?.surveyResponseEntityId;
  const storedSurveySkipped = getSessionStorageItem<boolean>('surveySkipped');
  // Survey always happens after EULA selection
  const bothEulaAndSurveyDone = hasSubmittedSurvey || storedSurveySkipped;

  useEffect(() => {
    if (!setUserOrganisations) return;

    // While the session is recovering, leave the membership list untouched
    // (its loading `undefined` state) — `PrivateGate` will refresh the token
    // or redirect, after which memberships are refetched. Forcing `[]` here
    // would masquerade as "no memberships" and trigger a /restricted redirect.
    if (sessionRecovering) return;

    // Resolve to an empty array (rather than leaving it unresolved) when the
    // fetch errored or the session is blocked without an active recovery (e.g.
    // bootstrap produced no user context) — the fetch is disabled, so `data`
    // never arrives. Downstream consumers (e.g. the registry layout) can then
    // transition out of their loading state instead of hanging on `undefined`.
    if (orgFetchError || sessionBlocksProfile) {
      setUserOrganisations([]);
      return;
    }

    if (!data) return;

    const uniqueOrganisations = data.data.reduce(
      (acc: RoleUserOrgAssociationDto[], org) => {
        if (
          !acc.some(
            (existingOrg) => existingOrg.organisationId === org.organisationId,
          )
        ) {
          acc.push(org);
        }
        return acc;
      },
      [],
    );

    setUserOrganisations(uniqueOrganisations);

    if (bothEulaAndSurveyDone) {
      onUserOrgAssociationsRetrieved(uniqueOrganisations);
    }

    //eslint-disable-next-line
  }, [data, orgFetchError, sessionBlocksProfile, sessionRecovering]);

  const fetchUserProfile = () => {
    if (loginFlowComplete && userId && userProfileApi) {
      return userProfileApi.apiUserProfileGet(userId);
    }
  };

  // Also gate the profile fetch on `sessionBlocksProfile`: a stale-but-truthy
  // `userId` (e.g. bootstrap failed after a token refresh) would otherwise let
  // the profile load and fire `onUserProfileLoaded` while the session is in an
  // error state, contradicting the degraded UI that `onUserProfileError(true)`
  // is asserting.
  const { data: userProfileResponse, error: userProfileFetchError } = useSWR(
    userId && !sessionBlocksProfile ? `/api/UserProfile/${userId}` : null,
    fetchUserProfile,
    {
      onError: (error) => {
        logError(
          '[BREEDLOGIC] <PostLogicFlow> Error fetching user profile',
          error,
        );
      },
    },
  );

  const hasProfileError =
    Boolean(userProfileFetchError) || sessionBlocksProfile;

  useEffect(() => {
    // Don't apply a (possibly stale) profile while erroring — the error path
    // below clears `userProfile` so the header shows its fallback. Without this
    // guard, SWR's cached `data` would immediately re-populate the profile that
    // the error handler just cleared.
    if (hasProfileError) return;

    if (
      userProfileResponse?.data &&
      userProfileResponse?.data !== userProfile
    ) {
      onUserProfileLoaded(userProfileResponse.data);
    }
  }, [onUserProfileLoaded, userProfileResponse, hasProfileError]); // eslint-disable-line react-hooks/exhaustive-deps

  // Surface the profile fetch outcome so consumers (e.g. the header avatar)
  // don't stay in a loading state indefinitely. This covers both a failing
  // profile endpoint (SWR retries the 4xx/5xx on a backoff) and a session that
  // can't yield a profile at all (`sessionBlocksProfile` — e.g. bootstrap
  // returned no user id, so the fetch never even starts). The consumer also
  // clears any stale `userProfile` when this is `true`.
  useEffect(() => {
    onUserProfileError(hasProfileError);
  }, [onUserProfileError, hasProfileError]);

  return null;
}
